/**
 * The menu — CLAUDE.md §4.12. The areas are the hierarchy: everything on one surface, nothing
 * behind a disclosure triangle, and only Production Control divides into stages, drawn as the
 * production route a supervisor already holds. Collapsed, this becomes an icon rail.
 */

import { useState } from 'react';
import { faHouse, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useReportWorkspace } from '@/lib/reportWorkspace';
import {
  areaItems,
  countMenuItems,
  menuForRoles,
  searchMenu,
  type MenuArea,
  type MenuGroup,
  type MenuItem,
} from '@/lib/menu';
import { cn } from '@/lib/utils';

/** Compact rows — more of the menu visible without scrolling. */
const ROW = 'flex min-h-7 items-center gap-2 rounded-md px-2 text-xs';

const FOCUS =
  'outline-none focus-visible:ring-2 focus-visible:ring-shell-foreground/40 focus-visible:ring-offset-1 focus-visible:ring-offset-shell';

export function NavMenu({
  onNavigate,
  collapsed = false,
  onExpand,
}: {
  onNavigate?: () => void;
  /** Desktop icon rail — labels and search hidden. */
  collapsed?: boolean;
  /** Called when a collapsed icon needs the full menu (e.g. area with reports). */
  onExpand?: () => void;
}) {
  const { user } = useAuth();
  const { text } = useI18n();
  const [term, setTerm] = useState('');

  const menu = menuForRoles(user?.roles ?? []);
  const total = countMenuItems(menu);
  const searching = term.trim() !== '';
  const visible = searchMenu(menu, term);

  if (collapsed) {
    return (
      <nav aria-label={text('Reports')} className="flex flex-col items-center gap-0.5 px-1 py-2">
        <CollapsedIcon
          label={text('Overview')}
          to="/"
          end
          icon={faHouse}
          onNavigate={onNavigate}
        />
        {menu.map((area) => (
          <CollapsedAreaIcon
            key={area.id}
            area={area}
            onExpand={onExpand}
            onNavigate={onNavigate}
          />
        ))}
      </nav>
    );
  }

  return (
    <div className="flex flex-col gap-2 py-2">
      {total > 0 && (
        <div className="px-2">
          <Label htmlFor="menu-filter" className="sr-only">
            {text('Filter reports')}
          </Label>
          <div className="relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-[10px] text-shell-muted"
              aria-hidden
            />
            <Input
              id="menu-filter"
              type="search"
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
              }}
              placeholder={text('Filter reports')}
              className="h-7 border-shell-border bg-shell-hover/40 py-0 pl-7 text-xs text-shell-foreground placeholder:text-shell-muted focus-visible:border-shell-muted"
            />
          </div>
        </div>
      )}

      <nav aria-label={text('Reports')} className="px-2">
        <NavLink to="/" end onClick={onNavigate} className={navLinkClass}>
          {text('Overview')}
        </NavLink>

        {searching && visible.length === 0 && (
          <p className="px-2 py-3 text-xs text-shell-muted">
            No reports match that. Try the area name — production, quality, delivery.
          </p>
        )}

        {visible.length > 0 && (
          <>
            {searching && <SectionLabel>Matches</SectionLabel>}
            <ul className={cn('space-y-0.5', !searching && 'pt-2')}>
              {visible.map((area) => (
                <AreaBlock
                  key={area.id}
                  area={area}
                  onNavigate={onNavigate}
                  searching={searching}
                />
              ))}
            </ul>
          </>
        )}

        {total === 0 && (
          <p className="px-2 pt-3 text-[11px] leading-relaxed text-shell-muted">
            Reports appear under their area as they are ported.
          </p>
        )}
      </nav>
    </div>
  );
}

function CollapsedIcon({
  label,
  to,
  end,
  icon,
  onNavigate,
}: {
  label: string;
  to: string;
  end?: boolean;
  icon: typeof faHouse;
  onNavigate?: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={to}
          end={end}
          onClick={onNavigate}
          aria-label={label}
          className={({ isActive }) =>
            cn(
              FOCUS,
              'flex size-8 items-center justify-center rounded-md transition-colors',
              isActive
                ? 'bg-shell-active text-shell-foreground'
                : 'text-shell-muted hover:bg-shell-hover hover:text-shell-foreground',
            )
          }
        >
          <FontAwesomeIcon icon={icon} className="text-[13px]" aria-hidden />
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function CollapsedAreaIcon({
  area,
  onExpand,
  onNavigate,
}: {
  area: MenuArea;
  onExpand?: () => void;
  onNavigate?: () => void;
}) {
  const { hrefFor } = useReportWorkspace();
  const reports = areaItems(area);
  const empty = reports.length === 0;
  const only = reports.length === 1 ? reports[0] : undefined;

  const className = cn(
    FOCUS,
    'flex size-8 items-center justify-center rounded-md transition-colors',
    empty
      ? 'cursor-default text-shell-muted/40'
      : 'text-shell-muted hover:bg-shell-hover hover:text-shell-foreground',
  );

  const icon = (
    <FontAwesomeIcon icon={area.icon} className="text-[13px]" aria-hidden />
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {empty ? (
          <span className={className} aria-label={area.label}>
            {icon}
          </span>
        ) : only !== undefined ? (
          <NavLink
            to={hrefFor(only.reportId)}
            onClick={onNavigate}
            aria-label={only.label}
            className={({ isActive }) =>
              cn(
                className,
                isActive && 'bg-shell-active text-shell-foreground',
              )
            }
          >
            {icon}
          </NavLink>
        ) : (
          <button
            type="button"
            aria-label={`${area.label} — expand menu`}
            className={className}
            onClick={() => {
              onExpand?.();
            }}
          >
            {icon}
          </button>
        )}
      </TooltipTrigger>
      <TooltipContent side="right">
        {empty ? `${area.label} (not yet ported)` : area.label}
      </TooltipContent>
    </Tooltip>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="px-2 pt-3 pb-1 text-[10px] font-medium tracking-wide text-shell-muted uppercase">
      {children}
    </p>
  );
}

function AreaBlock({
  area,
  onNavigate,
  searching,
}: {
  area: MenuArea;
  onNavigate?: () => void;
  /** While filtering, the route hairline is dropped — the sequence is not the point. */
  searching: boolean;
}) {
  const { text } = useI18n();
  const empty = areaItems(area).length === 0;
  const asRoute = area.sequential === true && !searching;

  return (
    <li>
      <div className={cn(ROW, 'gap-1.5 py-0.5')}>
        <FontAwesomeIcon
          icon={area.icon}
          className={cn(
            'w-3.5 shrink-0 text-center text-[11px]',
            empty ? 'text-shell-muted/50' : 'text-shell-foreground',
          )}
          aria-hidden
        />
        <span
          className={cn(
            'text-xs font-semibold tracking-tight',
            empty ? 'text-shell-muted' : 'text-shell-foreground',
          )}
        >
          {text(area.label)}
        </span>
      </div>

      {area.items.length > 0 && <ReportList items={area.items} onNavigate={onNavigate} />}

      {area.groups.length > 0 && (
        <ul
          className={cn(
            'mb-0.5 ml-4 space-y-0.5',
            // The hairline is the route: it runs down the stages and stops there.
            asRoute && 'border-l border-shell-border',
          )}
        >
          {area.groups.map((group) => (
            <GroupBlock
              key={group.id}
              group={group}
              onNavigate={onNavigate}
              onRoute={asRoute}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function GroupBlock({
  group,
  onNavigate,
  onRoute = false,
}: {
  group: MenuGroup;
  onNavigate?: () => void;
  onRoute?: boolean;
}) {
  const { text } = useI18n();
  const empty = group.items.length === 0;

  return (
    <li className={cn(onRoute && 'relative pl-3')}>
      {/* A node on the route spine, one per stage. Filled where reports exist, hollow where the
          stage is still empty, so what is ported reads at a glance without a legend. */}
      {onRoute && (
        <span
          className={cn(
            'absolute top-2.5 -left-[3.5px] size-1.5 rounded-full border-2 border-shell',
            empty ? 'bg-shell-muted/40' : 'bg-shell-foreground',
          )}
          aria-hidden
        />
      )}

      <div className={cn(ROW, 'gap-1.5 py-0.5')}>
        <FontAwesomeIcon
          icon={group.icon}
          className={cn(
            'w-3.5 shrink-0 text-center text-[11px]',
            empty ? 'text-shell-muted/50' : 'text-shell-foreground',
          )}
          aria-hidden
        />
        <span
          className={cn(
            'text-xs font-medium tracking-tight',
            empty ? 'text-shell-muted' : 'text-shell-foreground',
          )}
        >
          {text(group.label)}
        </span>
      </div>

      {!empty && <ReportList items={group.items} onNavigate={onNavigate} />}
    </li>
  );
}

function ReportList({
  items,
  onNavigate,
}: {
  items: readonly MenuItem[];
  onNavigate?: () => void;
}) {
  const { hrefFor } = useReportWorkspace();
  const { text } = useI18n();

  return (
    <ul className="mb-0.5 ml-5 space-y-0">
      {items.map((item) => (
        <li key={item.reportId}>
          <NavLink
            to={hrefFor(item.reportId)}
            onClick={onNavigate}
            className={navLinkClass}
          >
            {text(item.label)}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    ROW,
    FOCUS,
    'transition-colors',
    isActive
      ? 'bg-shell-active font-medium text-shell-foreground'
      : 'text-shell-muted hover:bg-shell-hover hover:text-shell-foreground',
  );
}
